import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './Unsubscribe.scss';

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('confirm'); // 'confirm', 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const email = searchParams.get('email');

  const validateReason = (text) => {
    // Remove whitespace and check length
    const trimmed = text.trim();

    if (trimmed.length === 0) {
      return 'Gelieve een reden op te geven.';
    }

    if (trimmed.length < 10) {
      return 'Gelieve minstens 10 karakters in te vullen.';
    }

    // Check if it's mostly gibberish (less than 2 spaces suggests no real words)
    const wordCount = trimmed.split(/\s+/).length;
    if (wordCount < 2) {
      return 'Gelieve een duidelijke reden op te geven met meerdere woorden.';
    }

    // Check for repeated characters (like "aaaaaaa")
    if (/(.)\1{4,}/.test(trimmed)) {
      return 'Gelieve een duidelijke reden op te geven.';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    if (!email) {
      setStatus('error');
      setMessage('Geen geldig e-mailadres opgegeven.');
      return;
    }

    // Validate reason
    const validationError = validateReason(reason);
    if (validationError) {
      setReasonError(validationError);
      return;
    }

    setStatus('loading');
    setReasonError('');

    try {
      const response = await fetch(`/api/v1/unsubscribe?email=${encodeURIComponent(email)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: reason.trim() }),
      });

      if (response.ok) {
        setStatus('success');
      } else {
        const data = await response.json();
        setStatus('error');
        setMessage(data.message || 'Er is iets misgegaan. Probeer het later opnieuw.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Er is een fout opgetreden. Probeer het later opnieuw.');
    }
  };

  const handleReasonChange = (e) => {
    setReason(e.target.value);
    setReasonError('');
  };

  return (
    <div className="unsubscribe-container">
      <div className="unsubscribe-card">
        <div className="otos-logo">OTOS</div>

        {status === 'confirm' && (
          <div className="status-content">
            <h1>Afmelden voor enquête-herinneringen</h1>
            <p className="email-text">E-mail: <strong>{email}</strong></p>

            <div className="info-box importance">
              <p>
                <strong>De gegevens uit onze follow-up enquêtes zijn van grote waarde voor ons.</strong> Ze helpen ons om de kwaliteit van onze zorg te verbeteren en betere resultaten voor toekomstige patiënten te bereiken.
              </p>
            </div>

            <p>We vinden het jammer dat u zich wilt afmelden. Om onze dienstverlening te kunnen verbeteren, willen we graag begrijpen waarom u deze keuze maakt.</p>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="reason">Waarom wilt u zich afmelden? *</label>
                <textarea
                  id="reason"
                  className={`form-control ${reasonError ? 'is-invalid' : ''}`}
                  rows="4"
                  placeholder="Laat ons weten waarom u niet langer wenst deel te nemen aan de enquête over uw behandeling"
                  value={reason}
                  onChange={handleReasonChange}
                  maxLength="500"
                />
                {reasonError && (
                  <div className="error-message">{reasonError}</div>
                )}
                <small className="form-text">Minimum 10 karakters vereist</small>
              </div>

              <button type="submit" className="btn btn-danger">
                Bevestig afmelding
              </button>
            </form>

            <div className="contact-info">
              <p>Of neem rechtstreeks contact op met:</p>
              <p><strong>Sint-Jozefskliniek Izegem</strong></p>
              <p>Tel: +32 51 33 47 00</p>
              <p>Email: orthopedie@sjki.be</p>
            </div>
          </div>
        )}

        {status === 'loading' && (
          <div className="status-content">
            <h1>Een moment geduld...</h1>
            <p>Uw verzoek wordt verwerkt.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="status-content success">
            <div className="checkmark">✓</div>
            <h1>Afmelding geslaagd</h1>
            <p>U bent succesvol afgemeld voor enquête-herinneringen.</p>
            <p className="email-text">E-mail: <strong>{email}</strong></p>
            <div className="info-box">
              <p>
                Uw afmeldingsverzoek is geregistreerd en wordt binnen 24 uur verwerkt.
                U ontvangt mogelijk nog één herinnering als deze reeds was ingepland.
              </p>
              <p className="mt-3">
                Bedankt voor uw feedback. Dit helpt ons om onze dienstverlening te verbeteren.
              </p>
            </div>
            <div className="contact-info">
              <p>Voor vragen kunt u contact opnemen met:</p>
              <p><strong>Sint-Jozefskliniek Izegem</strong></p>
              <p>Tel: +32 51 33 47 00</p>
              <p>Email: orthopedie@sjki.be</p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="status-content error">
            <div className="error-icon">✕</div>
            <h1>Er is een probleem opgetreden</h1>
            <p>{message}</p>
            <div className="contact-info">
              <p>Neem contact op met:</p>
              <p><strong>orthopedie@sjki.be</strong></p>
              <p>Tel: +32 51 33 47 00</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
